import { useState, useEffect, useCallback, useRef } from 'react';
import { api, JobStatus } from '../services/api';

interface UseJobStatusReturn {
  status: JobStatus['status'] | null;
  isRunning: boolean;
  progress: {
    current: number;
    total: number;
    percentage: number;
    message: string | null;
  } | null;
  job: JobStatus | null;
  /** Alias for job?.resultSummary — convenience for programme frontends */
  result: Record<string, any> | null;
  error: string | null;
  startJob: (url: string, params?: Record<string, any>) => Promise<void>;
  reset: () => void;
}

/**
 * Tracks a job's status by polling the jobs API.
 *
 * Two usage patterns:
 * 1. External start: pass a jobId obtained from your own api.post() call.
 *    The hook auto-polls as soon as jobId is non-null.
 * 2. Internal start: pass null initially, then call startJob(url, params).
 *    The hook starts the job via api.post(url) and polls the returned jobId.
 */
/**
 * Aufeinanderfolgende Poll-Fehler bis zum Aufgeben. Transiente Fehler (Netz-Hickup,
 * Token-Refresh, 304-Revalidierung) duerfen das Polling NICHT dauerhaft stoppen —
 * sonst friert die UI fuer immer in "running" ein, obwohl der Job laengst fertig ist
 * (Bug 17.07.2026: kaufland-Scan-UI hing nach einem einzelnen fehlgeschlagenen Poll).
 */
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

export function useJobStatus(jobId: string | null): UseJobStatusReturn {
  const [job, setJob] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failCountRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async (id: string) => {
    try {
      const status = await api.getJobStatus(id);
      failCountRef.current = 0;
      setJob(status);

      if (status.status === 'completed' || status.status === 'failed') {
        stopPolling();
        if (status.status === 'failed' && status.error) {
          setError(status.error);
        }
      }
    } catch (err: any) {
      // Ein 304 (Browser-Cache-Revalidierung) ist gar kein Fehler — Body unveraendert,
      // letzter bekannter Stand gilt weiter. Einfach weiterpollen.
      if (err?.response?.status === 304) return;
      failCountRef.current += 1;
      if (failCountRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
        setError(err.message || 'Failed to fetch job status');
        stopPolling();
      }
      // sonst: transient — naechster Intervall-Tick versucht es erneut
    }
  }, [stopPolling]);

  // Auto-poll whenever jobId changes (external job start pattern)
  useEffect(() => {
    stopPolling();
    setJob(null);
    setError(null);
    failCountRef.current = 0;

    if (!jobId) return;

    poll(jobId);
    intervalRef.current = setInterval(() => poll(jobId), 1000);

    return stopPolling;
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startJob = useCallback(async (url: string, params?: Record<string, any>) => {
    setError(null);
    setJob(null);
    stopPolling();
    failCountRef.current = 0;

    try {
      const res = await api.post<{ success: boolean; data: { jobId: string } }>(url, params);
      const newJobId = res.data?.data?.jobId;
      if (!newJobId) throw new Error('Keine Job-ID erhalten');

      // Start polling — stops automatically on completion/failure
      intervalRef.current = setInterval(() => poll(newJobId), 1000);
      poll(newJobId);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to start job');
    }
  }, [poll, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setJob(null);
    setError(null);
    failCountRef.current = 0;
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => stopPolling, [stopPolling]);

  const progress = job && (job.progressTotal > 0 || job.progressMessage) ? {
    current: job.progressCurrent,
    total: job.progressTotal,
    percentage: job.progressTotal > 0 ? Math.round((job.progressCurrent / job.progressTotal) * 100) : 0,
    message: job.progressMessage,
  } : null;

  return {
    status: job?.status ?? null,
    // error gesetzt = Polling hat aufgegeben (oder Job failed) — dann NICHT mehr als
    // "laeuft" melden, sonst bleiben Buttons/Spinner ewig gesperrt (Bug 17.07.2026).
    isRunning: (job?.status === 'running' || job?.status === 'pending') && !error,
    progress,
    job,
    result: job?.resultSummary ?? null,
    error,
    startJob,
    reset,
  };
}
